import { getMerchantToken } from "./merchantAuthApi";

const API_BASE_URL = (
  process.env.REACT_APP_MERCHANT_API_BASE_URL ||
  "http://secondbite-manage.localhost/merchant-api/v1"
).replace(/\/+$/, "");

const request = async (path, options = {}) => {
  let response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${getMerchantToken()}`,
      },
    });
  } catch {
    throw new Error("Unable to connect to the SecondBite backend.");
  }
  const payload = await response.json().catch(() => null);
  if (!response.ok || !payload?.success) {
    const validationMessage = payload?.errors ? Object.values(payload.errors).flat()[0] : null;
    throw new Error(validationMessage || payload?.message || "Unable to process outlet settings.");
  }
  return payload.data;
};

export const getOutletProductSettings = (outletId) =>
  request(`/outlet-product-settings/${outletId}`);

export const updateOutletSettings = (outletId, data) =>
  request(`/outlet-product-settings/${outletId}/outlet`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const updateProductSettings = (outletId, productId, data) =>
  request(`/outlet-product-settings/${outletId}/products/${productId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error("Unable to read the compressed image."));
  reader.readAsDataURL(blob);
});

const canvasToBlob = (canvas, type, quality) => new Promise((resolve, reject) => {
  canvas.toBlob(
    (blob) => blob ? resolve(blob) : reject(new Error("Unable to compress the selected image.")),
    type,
    quality
  );
});

export const compressImageFile = async (file, options = {}) => {
  if (!file) return null;
  if (!file.type.startsWith("image/")) throw new Error("Please select a valid image file.");
  if (file.size > 12 * 1024 * 1024) throw new Error("The original image must not exceed 12 MB.");

  const {
    maxWidth = 1600,
    maxHeight = 1000,
    maxBytes = 650 * 1024,
    quality = 0.84,
  } = options;
  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error("Unable to decode the selected image."));
      element.src = objectUrl;
    });

    const scale = Math.min(1, maxWidth / image.naturalWidth, maxHeight / image.naturalHeight);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = canvas.getContext("2d", { alpha: true });
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    const outputType = file.type === "image/png" ? "image/webp" : "image/jpeg";
    let outputQuality = quality;
    let blob = await canvasToBlob(canvas, outputType, outputQuality);

    while (blob.size > maxBytes && outputQuality > 0.5) {
      outputQuality -= 0.08;
      blob = await canvasToBlob(canvas, outputType, outputQuality);
    }

    return blobToDataUrl(blob);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};
